import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { GeoNode } from '../../../features/users/users.model';

interface FlatNode {
  node: GeoNode;
  depth: number;
}

@Component({
  selector: 'app-geographic-territory-tree',
  standalone: false,
  templateUrl: './geographic-territory-tree.component.html',
  styleUrl: './geographic-territory-tree.component.scss'
})
export class GeographicTerritoryTreeComponent {
  @Input() selectedIds: string[] = [];
  @Input() nodes: GeoNode[] = [];
  @Input() isModified = false;
  @Input() originalNodeCount = 0;
  @Input() loading = false;

  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() searchChange = new EventEmitter<string>();

  searchQuery = '';
  isOpen = false;

  constructor(private elRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  get triggerLabel(): string {
    const n = this.selectedIds.length;
    return n === 0 ? 'Select territories…' : `${n} territor${n > 1 ? 'ies' : 'y'} selected`;
  }

  readonly COLOR_MAP: Record<string, string> = {
    teal: '#00C9B1',
    orange: '#F59E0B',
    purple: '#8B5CF6',
    blue: '#3B82F6',
    red: '#EF4444'
  };

  get filteredNodes(): GeoNode[] {
    return this.nodes;
  }

  get flatNodes(): FlatNode[] {
    return this.flattenNodes(this.filteredNodes, 0);
  }

  onSearchInput(): void {
    this.searchChange.emit(this.searchQuery);
  }

  private flattenNodes(nodes: GeoNode[], depth: number): FlatNode[] {
    const result: FlatNode[] = [];
    for (const node of nodes) {
      result.push({ node, depth });
      if (node.expanded !== false && node.children?.length) {
        result.push(...this.flattenNodes(node.children, depth + 1));
      }
    }
    return result;
  }

  isChecked(node: GeoNode): boolean {
    if (!node.children?.length) return this.selectedIds.includes(node.id);
    const leafIds = this.getAllLeafIds(node);
    return leafIds.length > 0 && leafIds.every(id => this.selectedIds.includes(id));
  }

  isIndeterminate(node: GeoNode): boolean {
    if (!node.children?.length) return false;
    const leafIds = this.getAllLeafIds(node);
    const selectedLeaves = leafIds.filter(id => this.selectedIds.includes(id));
    return selectedLeaves.length > 0 && selectedLeaves.length < leafIds.length;
  }

  private getAllLeafIds(node: GeoNode): string[] {
    if (!node.children?.length) return [node.id];
    return node.children.flatMap(child => this.getAllLeafIds(child));
  }

  getColorHex(color: string | undefined): string {
    if (!color) return '#94A3B8';
    return this.COLOR_MAP[color] ?? '#94A3B8';
  }

  toggleNode(node: GeoNode): void {
    const leafIds = this.getAllLeafIds(node);
    const allSelected = leafIds.every(id => this.selectedIds.includes(id));
    let updated = [...this.selectedIds];
    if (allSelected) {
      updated = updated.filter(id => !leafIds.includes(id));
    } else {
      leafIds.forEach(id => { if (!updated.includes(id)) updated.push(id); });
    }
    this.selectedIds = updated;
    this.selectionChange.emit(updated);
  }

  toggleExpanded(node: GeoNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }
}
